import { FunctionComponent, MouseEvent, useState } from "react";

import { faCalendarDays, faEnvelope } from "@fortawesome/pro-regular-svg-icons";
import { isBouncedEmailStatus, isSendableEmailStatus } from "@signalist/shared-types";

import { Icon } from "@/components";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import { EmailIcon } from "@/components/EmailIcon/EmailIcon";
import { getEmailIconStatus } from "@/components/EmailIcon/getEmailIconStatus";
import { getPhoneIconStatus, knownPhoneNumber } from "@/components/PhoneIcon/getPhoneIconStatus";
import { PhoneIcon } from "@/components/PhoneIcon/PhoneIcon";
import { LinkedinIcon } from "@/components/LinkedinIcon/LinkedinIcon";

import { ensureHttpProtocol } from "@/utils/url";
import { handleCopyClick } from "@/pages/Private/pages/List/utils/handleCopyClick";

import { ContactChannelData, ContactDomain } from "./types";
import { useContactChannelActions } from "./useContactChannelActions";
import { ContactChannelTooltip } from "./ContactChannelTooltip";
import { ContactEditModal } from "./ContactEditModal";

interface ContactChannelsProps {
	data: ContactChannelData;
	domain: ContactDomain;
	/** Copy handler — defaults to the shared clipboard helper. */
	onCopy?: (value: string) => void;
	/** Render the booking-link icon even when no link is set (defaults to false). */
	showBookingLink?: boolean;
	/** Open the lead's detail panel — wired to the pending-email indicator. */
	onOpenPanel?: () => void;
}

const stop = (e: MouseEvent) => {
	e.preventDefault();
	e.stopPropagation();
};

/**
 * Unified, surface-agnostic contact row: LinkedIn · Email · Phone · Booking link.
 * Hover an icon for the copy/status tooltip, click to act (open / mailto / tel)
 * or kick off enrichment, and use "Edit contact info" to open the shared modal.
 * Used inline per row on the Contacts table and every List/Messages surface.
 */
export const ContactChannels: FunctionComponent<ContactChannelsProps> = ({
	data,
	domain,
	onCopy = handleCopyClick,
	showBookingLink = false,
	onOpenPanel,
}) => {
	const [showEditModal, setShowEditModal] = useState(false);
	// Bumping this remounts the hover tooltips so the one that launched the edit
	// modal closes immediately — the Tooltip is hover-driven and never receives a
	// mouseleave once the modal backdrop covers the icon.
	const [tooltipNonce, setTooltipNonce] = useState(0);
	const { update, searchEmail, searchPhone, isUpdating } = useContactChannelActions(domain);

	const openEdit = () => {
		setTooltipNonce((nonce) => nonce + 1);
		setShowEditModal(true);
	};

	const handleEmailClick = (e: MouseEvent) => {
		stop(e);

		if (data.emailStatus === "SEARCHING") {
			return;
		}

		// A sendable address (VALID, RISKY) and both bounced states keep an address — open it in the
		// mail client. (Re-finding a bounced address is a no-op server-side, so don't waste a click.)
		if (
			data.email &&
			(isSendableEmailStatus(data.emailStatus) || isBouncedEmailStatus(data.emailStatus))
		) {
			window.location.href = `mailto:${data.email}`;

			return;
		}

		searchEmail(data.id);
	};

	const handlePhoneClick = (e: MouseEvent) => {
		stop(e);

		if (data.phoneStatus === "SEARCHING") {
			return;
		}

		const dialable = knownPhoneNumber(data.phone, data.phoneStatus);

		if (dialable) {
			window.location.href = `tel:${dialable.replace(/\s/g, "")}`;

			return;
		}

		searchPhone(data.id);
	};

	const handleLinkedinClick = (e: MouseEvent) => {
		stop(e);

		if (data.linkedinUrl) {
			window.open(ensureHttpProtocol(data.linkedinUrl), "_blank");

			return;
		}

		openEdit();
	};

	const handleBookingClick = (e: MouseEvent) => {
		stop(e);

		if (data.bookingLink) {
			window.open(ensureHttpProtocol(data.bookingLink), "_blank");

			return;
		}

		openEdit();
	};

	const renderBooking = data.bookingLink || showBookingLink;

	return (
		<div className="flex flex-row items-center" onClick={stop}>
			<Tooltip
				key={`linkedin-${tooltipNonce}`}
				content={
					<ContactChannelTooltip channel="linkedin" data={data} onCopy={onCopy} onEdit={openEdit} />
				}
			>
				<button
					aria-label="linkedin"
					className="flex h-[20px] cursor-pointer items-center"
					type="button"
					onClick={handleLinkedinClick}
				>
					<LinkedinIcon status={data.linkedinUrl ? "checked" : "failed"} />
				</button>
			</Tooltip>

			<Tooltip
				key={`email-${tooltipNonce}`}
				content={
					<ContactChannelTooltip
						channel="email"
						data={data}
						onCopy={onCopy}
						onEdit={openEdit}
						onSearch={() => searchEmail(data.id)}
					/>
				}
			>
				<button
					aria-label="email"
					className="flex h-[20px] cursor-pointer items-center"
					type="button"
					onClick={handleEmailClick}
				>
					<EmailIcon status={getEmailIconStatus(data.email, data.emailStatus)} />
				</button>
			</Tooltip>

			{domain === "list" && data.pendingEmails.length > 0 && (
				<button
					aria-label="pending emails"
					className={`ml-0.5 flex h-[20px] items-center gap-0.5 rounded-full bg-status-warning-light px-1 text-status-warning ${
						onOpenPanel ? "cursor-pointer" : "cursor-default"
					}`}
					type="button"
					onClick={(e) => {
						stop(e);
						onOpenPanel?.();
					}}
				>
					<Icon className="h-[12px] w-[12px]" icon={faEnvelope} />
					<span className="text-[10px] font-semibold leading-none">
						{data.pendingEmails.length}
					</span>
				</button>
			)}

			<Tooltip
				key={`phone-${tooltipNonce}`}
				content={
					<ContactChannelTooltip
						channel="phone"
						data={data}
						onCopy={onCopy}
						onEdit={openEdit}
						onSearch={() => searchPhone(data.id)}
					/>
				}
			>
				<button
					aria-label="phone"
					className="flex h-[20px] cursor-pointer items-center"
					type="button"
					onClick={handlePhoneClick}
				>
					<PhoneIcon status={getPhoneIconStatus(data.phone, data.phoneStatus)} />
				</button>
			</Tooltip>

			{renderBooking && (
				<Tooltip
					key={`booking-${tooltipNonce}`}
					content={
						<ContactChannelTooltip
							channel="booking"
							data={data}
							onCopy={onCopy}
							onEdit={openEdit}
						/>
					}
				>
					<button
						aria-label="booking link"
						className="mr-1 flex h-[20px] w-[20px] cursor-pointer items-center justify-center"
						type="button"
						onClick={handleBookingClick}
					>
						<Icon
							className={`w-[16px] h-[16px] ${data.bookingLink ? "text-blue-action" : "text-text-meta"}`}
							icon={faCalendarDays}
						/>
					</button>
				</Tooltip>
			)}

			{showEditModal && (
				<ContactEditModal
					data={data}
					isLoading={isUpdating}
					isOpen={showEditModal}
					onClose={() => setShowEditModal(false)}
					onSubmit={(values) => update(values)}
				/>
			)}
		</div>
	);
};
